import React from 'react';
import { useApp } from '../context/AppContext';
import {
  Wifi,
  WifiOff,
  Settings,
  Volume2,
  VolumeX,
  Heart,
  Crown,
  Home,
  Bell,
  Compass
} from 'lucide-react';
import { triggerHaptic } from '../services/purrSynthesizer';
import { AppLayer } from '../types';

export const NavigationRibbon: React.FC = () => {
  const {
    isOnline,
    latencyMs,
    networkMode,
    isPurring,
    togglePurr,
    liveBpm,
    setSettingsOpen,
    setNotificationModalOpen,
    setBioModalOpen,
    setAuthModalOpen,
    setAllDecksModalOpen,
    authUser,
    profile,
    activeLayer,
    setActiveLayer,
    isUpdateAvailable
  } = useApp();

  const getIdentityDetails = () => {
    const uid = (authUser?.userId || profile || 'lycan').toLowerCase();
    if (uid === 'echo' || authUser?.role === 'admin') {
      return {
        label: authUser?.displayName || 'Echo',
        icon: <Crown className="w-3 h-3 text-amber-400" />,
        color: 'bg-amber-500/15 border-amber-500/50 text-amber-300'
      };
    }
    const nameMap: Record<string, string> = {
      lycan: 'Lycan',
      mom: 'Lycan (Sharon)',
      onyx: 'Onyx',
      trey: 'Trey',
      family: 'Family'
    };
    return {
      label: authUser?.displayName || nameMap[uid] || 'Family',
      icon: <Home className="w-3 h-3 text-emerald-400" />,
      color: 'bg-emerald-500/15 border-emerald-500/50 text-emerald-300'
    };
  };

  const identity = getIdentityDetails();

  const handleLayerClick = (layer: AppLayer) => {
    triggerHaptic(20);
    setActiveLayer(layer);
  };

  return (
    <div className="shrink-0 flex flex-col z-40 bg-[#070a0f]/95 backdrop-blur-xl border-b border-white/10 shadow-lg">
      {/* Top Header Bar */}
      <header className="px-3 py-2 flex items-center justify-between gap-2 overflow-hidden">
        {/* Brand & Crest - Tap to open Deck Selector */}
        <button
          onClick={() => {
            triggerHaptic(20);
            setAllDecksModalOpen(true);
          }}
          className="flex items-center gap-2 shrink-0 min-w-0 text-left hover:opacity-90 active:scale-95 transition-all p-1 -m-1 rounded-xl"
          title="Open All Decks Selector"
        >
          <div className="relative w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-amber-500/20 to-sky-500/10 border border-fissal-gold/40 shadow-quantum-glow shrink-0">
            <svg
              className={`w-5 h-5 text-fissal-gold ${isPurring ? 'animate-spin-slow' : ''}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            <div className={`absolute w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-emerald-400' : 'bg-rose-400'}`} />
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h1 className="font-cinzel text-xs font-bold tracking-wider text-white">FISSAL</h1>
              <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-fissal-gold/20 text-fissal-goldLight border border-fissal-gold/40">PRIME</span>
            </div>
            <div className="flex items-center gap-1 text-[10px] text-slate-400">
              {isOnline ? (
                <span className="flex items-center gap-1 text-fissal-verdant truncate">
                  <Wifi className="w-2.5 h-2.5 shrink-0" /> {networkMode} {latencyMs > 0 && `(${latencyMs}ms)`}
                </span>
              ) : (
                <span className="flex items-center gap-1 text-rose-400">
                  <WifiOff className="w-2.5 h-2.5 shrink-0" /> Offline Shield
                </span>
              )}
            </div>
          </div>
        </button>

        {/* Right Controls */}
        <div className="flex items-center gap-1.5 shrink-0">
          {/* All Decks Quick Launcher */}
          <button
            onClick={() => {
              setAllDecksModalOpen(true);
              triggerHaptic(20);
            }}
            className="p-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 hover:text-white transition-all shrink-0"
            title="All Decks Selector"
          >
            <Compass className="w-3.5 h-3.5 text-fissal-gold" />
          </button>

          {/* Notification & Alert Settings */}
          <button
            onClick={() => {
              setNotificationModalOpen(true);
              triggerHaptic(20);
            }}
            className="p-1.5 rounded-lg bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 text-sky-300 hover:text-white transition-all shrink-0"
            title="Camera Notification & Alert Settings"
          >
            <Bell className="w-3.5 h-3.5" />
          </button>

          {/* Purr Engine (compact icon) */}
          <button
            onClick={togglePurr}
            className={`p-1.5 rounded-lg border transition-all flex items-center justify-center shrink-0 ${
              isPurring
                ? 'bg-fissal-gold/20 border-fissal-gold text-fissal-gold animate-purr shadow-quantum-glow'
                : 'bg-white/5 border-white/10 text-slate-400 hover:text-white'
            }`}
            title="Toggle Mechanical Purr Engine (42.8 Hz)"
          >
            {isPurring ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
          </button>

          {/* Bio-Sync Pill (only renders when actively measuring heart rate) */}
          {liveBpm && (
            <button
              onClick={() => {
                triggerHaptic(15);
                setBioModalOpen(true);
              }}
              className="p-1.5 rounded-lg border transition-all bg-rose-500/20 border-rose-500/50 text-rose-300 shrink-0"
              title="Bio-Sync Telemetry"
            >
              <Heart className="w-3.5 h-3.5 fill-rose-500/30 text-rose-400" />
            </button>
          )}

          {/* Profile Switcher Pill (Echo / Lycan / Onyx / Trey) */}
          <button
            onClick={() => {
              setAuthModalOpen(true);
              triggerHaptic(20);
            }}
            className={`px-2.5 py-1 rounded-full border text-xs font-medium flex items-center gap-1.5 transition-all shadow-sm shrink-0 ${identity.color}`}
            title="Switch Household Profile (Echo, Lycan, Onyx, Trey)"
          >
            {identity.icon}
            <span className="font-semibold text-[11px] max-w-[78px] truncate">{identity.label}</span>
          </button>

          {/* Settings Button */}
          <button
            onClick={() => {
              setSettingsOpen(true);
              triggerHaptic(20);
            }}
            className="p-1.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-300 hover:text-white transition-all relative shrink-0 shadow-sm"
            title="Settings & Updates"
            aria-label="Settings"
          >
            <Settings className="w-4 h-4 text-amber-300" />
            {isUpdateAvailable && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-[#070a0f]" />
            )}
          </button>
        </div>
      </header>

      {/* Suite Toggle Segmented Bar: Family Suite (Prioritized) & DevOps Suite (Open to All) */}
      <div className="bg-[#0b1017]/90 px-3 py-1.5 flex items-center justify-center border-t border-white/5">
        <div className="flex items-center gap-1.5 bg-black/50 p-1 rounded-xl border border-white/10 w-full max-w-md">
          {/* Family Suite (Prioritized First) */}
          <button
            onClick={() => handleLayerClick('family')}
            className={`flex-1 py-1.5 px-3 rounded-lg text-[11px] font-bold transition-all flex items-center justify-center gap-1.5 ${
              activeLayer === 'family'
                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-sm'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <span>🏡</span>
            <span>Family Suite</span>
          </button>

          {/* DevOps & Field Ops Suite (Accessible to anyone, mostly Echo) */}
          <button
            onClick={() => handleLayerClick('devops')}
            className={`flex-1 py-1.5 px-3 rounded-lg text-[11px] font-bold transition-all flex items-center justify-center gap-1.5 ${
              activeLayer === 'devops'
                ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-sm'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <span>🛠️</span>
            <span>DevOps & USB Ops</span>
          </button>
        </div>
      </div>
    </div>
  );
};
